/**
 * Company facts that more than one surface states: the founders and the
 * investors. The /about page, its markdown mirror and the Organization
 * JSON-LD (`lib/schema.ts`) all read them from here, so a name, title or
 * LinkedIn URL can only be wrong in one place.
 *
 * Names and roles stay out of the message catalogs on purpose: they are the
 * same in every language and reach translated copy as ICU arguments.
 */

export interface CompanyPerson {
  id: string
  /** Plain name, without title or degree. */
  name: string
  honorificPrefix?: string
  honorificSuffix?: string
  /** Role as shown on the leadership cards (CEO, CPO, …). */
  role?: string
  /** schema.org `jobTitle`. */
  jobTitle: string
  /** schema.org `affiliation` (an institution name). */
  affiliation?: string
  linkedin?: string
  group: "leadership" | "medical"
}

/** The six founders of Ahead Health, in display order. */
export const FOUNDERS: readonly CompanyPerson[] = [
  {
    id: "nick-lenten",
    name: "Nick Lenten",
    role: "CEO",
    jobTitle: "CEO",
    linkedin: "https://www.linkedin.com/in/nlenten/",
    group: "leadership",
  },
  {
    id: "sven-jaeschke",
    name: "Sven Jaeschke",
    honorificSuffix: "PhD",
    role: "CPO",
    jobTitle: "CPO",
    linkedin: "https://www.linkedin.com/in/svenjaeschke/",
    group: "leadership",
  },
  {
    id: "dennis-hermann",
    name: "Dennis Hermann",
    role: "COO",
    jobTitle: "COO",
    linkedin: "https://www.linkedin.com/in/dennishermann",
    group: "leadership",
  },
  {
    id: "vega-vinsensius",
    name: "Vega Vinsensius",
    honorificSuffix: "PhD",
    role: "CTO",
    jobTitle: "CTO",
    linkedin: "https://www.linkedin.com/in/vinsensius-vega/",
    group: "leadership",
  },
  {
    id: "olivio-donati",
    name: "Olivio Donati",
    honorificPrefix: "Prof. Dr.",
    jobTitle: "Radiologist",
    affiliation: "University of Zurich",
    group: "medical",
  },
  {
    id: "michael-fischer",
    name: "Michael Fischer",
    honorificPrefix: "Prof. Dr.",
    jobTitle: "Radiologist",
    affiliation: "University of Zurich",
    group: "medical",
  },
]

/** "Prof. Dr. Olivio Donati", "Sven Jaeschke, PhD". */
export function personDisplayName(person: CompanyPerson): string {
  const base = person.honorificPrefix
    ? `${person.honorificPrefix} ${person.name}`
    : person.name
  return person.honorificSuffix ? `${base}, ${person.honorificSuffix}` : base
}

/**
 * Name for running text and lists: titles kept, degree dropped, so a list
 * reads "Sven Jaeschke, Dennis Hermann" rather than "Sven Jaeschke, PhD,
 * Dennis Hermann".
 */
export function personListName(person: CompanyPerson): string {
  return person.honorificPrefix
    ? `${person.honorificPrefix} ${person.name}`
    : person.name
}

export const LEADERSHIP_FOUNDERS = FOUNDERS.filter(
  (p) => p.group === "leadership"
)
export const MEDICAL_FOUNDERS = FOUNDERS.filter((p) => p.group === "medical")

/** Venture investors, in the order the /about investor card shows them. */
export const INVESTOR_FUNDS = [
  { name: "RTP Global", logo: "/images/logos/rtp-global.svg" },
  { name: "3VC", logo: "/images/logos/3vc.svg" },
  { name: "TSIC", logo: "/images/logos/tinyvc.svg" },
  { name: "Pareto", logo: "/images/logos/pareto.png" },
] as const

/**
 * Notable angel investors. `affiliationKey` marks the one affiliation that
 * is translated (an `about` namespace key); the rest are proper names.
 */
export const ANGEL_INVESTORS: readonly {
  name: string
  affiliation: string
  affiliationKey?: "angelHuggingFaceAffiliation"
}[] = [
  { name: "Cody Gakpo", affiliation: "Liverpool FC" },
  {
    name: "Thomas Wolf",
    affiliation: "Hugging Face co-founder",
    affiliationKey: "angelHuggingFaceAffiliation",
  },
  { name: "Michiel Bakker", affiliation: "DeepMind" },
  { name: "Stef van Grieken", affiliation: "Cradle" },
  { name: "Kai Eberhardt", affiliation: "Oviva" },
  { name: "Sara Huerlimann", affiliation: "Zahnarztzentrum" },
]

/** Locale-aware "A, B and C". */
export function formatList(items: readonly string[], intlLocale: string) {
  return new Intl.ListFormat(intlLocale, {
    style: "long",
    type: "conjunction",
  }).format(items)
}
